import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SlidersHorizontal, X, Loader2, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import ProductCard from '../components/ProductCard';
import AgeVerificationModal, { isAdultCategory, isAgeVerified } from '../components/AgeVerificationModal';
import { getProducts, getCategories, getPriceRange } from '../lib/api';

const PAGE_SIZE = 24;

function PriceRangeSlider({ min, max, valueMin, valueMax, onChange }) {
  const trackRef = useRef(null);

  const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

  const handlePointer = (which) => (e) => {
    e.preventDefault();
    const track = trackRef.current;
    if (!track) return;

    const move = (ev) => {
      const rect = track.getBoundingClientRect();
      const pct = clamp((ev.clientX - rect.left) / rect.width, 0, 1);
      const val = Math.round(min + pct * (max - min));
      if (which === 'min') onChange(Math.min(val, valueMax - 1), valueMax);
      else onChange(valueMin, Math.max(val, valueMin + 1));
    };
    const up = () => { document.removeEventListener('pointermove', move); document.removeEventListener('pointerup', up); };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  };

  const range = max - min || 1;
  const leftPct = ((valueMin - min) / range) * 100;
  const rightPct = ((valueMax - min) / range) * 100;

  return (
    <div className="pt-2 pb-1">
      <div ref={trackRef} className="relative h-2 bg-slate-200 rounded-full cursor-pointer" data-testid="price-slider-track">
        <div
          className="absolute h-full bg-[#003366] rounded-full"
          style={{ left: `${leftPct}%`, width: `${rightPct - leftPct}%` }}
        />
        <div
          className="absolute w-5 h-5 bg-white border-2 border-[#003366] rounded-full -translate-x-1/2 -translate-y-1/4 cursor-grab active:cursor-grabbing shadow-sm"
          style={{ left: `${leftPct}%` }}
          onPointerDown={handlePointer('min')}
          data-testid="price-slider-min-thumb"
        />
        <div
          className="absolute w-5 h-5 bg-white border-2 border-[#003366] rounded-full -translate-x-1/2 -translate-y-1/4 cursor-grab active:cursor-grabbing shadow-sm"
          style={{ left: `${rightPct}%` }}
          onPointerDown={handlePointer('max')}
          data-testid="price-slider-max-thumb"
        />
      </div>
      <div className="flex justify-between mt-2 text-sm text-slate-600">
        <span data-testid="price-min-label">${valueMin}</span>
        <span data-testid="price-max-label">${valueMax}</span>
      </div>
    </div>
  );
}

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showAgeModal, setShowAgeModal] = useState(false);
  const [pendingCategory, setPendingCategory] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Price range bounds
  const [priceBounds, setPriceBounds] = useState({ min: 0, max: 1000 });

  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    search: searchParams.get('search') || '',
    deal: searchParams.get('deal') === 'true',
    featured: searchParams.get('featured') === 'true',
    minPrice: searchParams.get('minPrice') ? Number(searchParams.get('minPrice')) : null,
    maxPrice: searchParams.get('maxPrice') ? Number(searchParams.get('maxPrice')) : null,
    sort: searchParams.get('sort') || 'newest',
  });

  const observerRef = useRef(null);
  const sentinelRef = useRef(null);
  const skipRef = useRef(0);

  // Age verification on load
  useEffect(() => {
    const cat = searchParams.get('category');
    if (cat && isAdultCategory(cat) && !isAgeVerified()) {
      setPendingCategory(cat);
      setShowAgeModal(true);
      setFilters(prev => ({ ...prev, category: '' }));
    }
  }, []);

  // Fetch categories + price range
  useEffect(() => {
    getCategories().then(res => setCategories(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    getPriceRange(filters.category ? { category: filters.category } : {})
      .then(res => {
        const b = { min: Math.floor(res.data.min), max: Math.ceil(res.data.max) };
        setPriceBounds(b);
        // Initialize slider values if not set by URL
        setFilters(prev => ({
          ...prev,
          minPrice: prev.minPrice ?? b.min,
          maxPrice: prev.maxPrice ?? b.max,
        }));
      })
      .catch(() => {});
  }, [filters.category]);

  // Fetch products (initial / filter change)
  const fetchProducts = useCallback(async (reset = true) => {
    if (reset) {
      setLoading(true);
      skipRef.current = 0;
    } else {
      setLoadingMore(true);
    }

    const params = { skip: skipRef.current, limit: PAGE_SIZE };
    if (filters.category) params.category = filters.category;
    if (filters.search) params.search = filters.search;
    if (filters.deal) params.deal = true;
    if (filters.featured) params.featured = true;
    if (filters.minPrice != null && filters.minPrice > priceBounds.min) params.min_price = filters.minPrice;
    if (filters.maxPrice != null && filters.maxPrice < priceBounds.max) params.max_price = filters.maxPrice;
    if (filters.sort) params.sort = filters.sort;

    try {
      const res = await getProducts(params);
      const { products: fetched, total: t, has_more } = res.data;
      if (reset) {
        setProducts(fetched);
      } else {
        setProducts(prev => [...prev, ...fetched]);
      }
      setTotal(t);
      setHasMore(has_more);
      skipRef.current += fetched.length;
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filters, priceBounds]);

  // Re-fetch when filters change
  useEffect(() => {
    fetchProducts(true);
  }, [filters.category, filters.search, filters.deal, filters.featured, filters.sort, filters.minPrice, filters.maxPrice]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchProducts(false);
        }
      },
      { rootMargin: '200px' }
    );

    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [hasMore, loadingMore, loading, fetchProducts]);

  // Scroll-to-top button
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const updateFilter = (key, value) => {
    if (key === 'category' && isAdultCategory(value) && !isAgeVerified()) {
      setPendingCategory(value);
      setShowAgeModal(true);
      return;
    }
    setFilters(prev => ({ ...prev, [key]: value }));
    const newParams = new URLSearchParams(searchParams);
    if (value != null && value !== '' && value !== false) {
      newParams.set(key, value.toString());
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const handleAgeVerified = () => {
    setShowAgeModal(false);
    if (pendingCategory) {
      setFilters(prev => ({ ...prev, category: pendingCategory }));
      const newParams = new URLSearchParams(searchParams);
      newParams.set('category', pendingCategory);
      setSearchParams(newParams);
      setPendingCategory(null);
    }
  };

  const handleAgeCanceled = () => {
    setShowAgeModal(false);
    setPendingCategory(null);
    if (searchParams.get('category') && isAdultCategory(searchParams.get('category'))) {
      navigate('/products');
    }
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      search: '',
      deal: false,
      featured: false,
      minPrice: priceBounds.min,
      maxPrice: priceBounds.max,
      sort: 'newest',
    });
    setSearchParams({});
  };

  const activeFilterCount = [
    filters.category,
    filters.search,
    filters.deal,
    filters.featured,
    filters.minPrice != null && filters.minPrice > priceBounds.min,
    filters.maxPrice != null && filters.maxPrice < priceBounds.max,
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="products-page">
      <div className="max-w-[1440px] mx-auto px-4 py-6">
        {/* Page header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
              {filters.search ? `Results for "${filters.search}"` :
               filters.deal ? "Today's Deals" :
               filters.featured ? "Featured Products" :
               filters.category ? categories.find(c => c.id === filters.category)?.name || 'Products' :
               'All Products'}
            </h1>
            <p className="text-slate-500" data-testid="product-count">{total} products found</p>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden"
              data-testid="toggle-filters-btn"
            >
              <SlidersHorizontal size={18} className="mr-2" />
              Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
            </Button>

            <Select value={filters.sort} onValueChange={(v) => updateFilter('sort', v)}>
              <SelectTrigger className="w-[180px]" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
                <SelectItem value="rating">Best Rated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Filters sidebar */}
          <aside className={`
            ${showFilters ? 'fixed inset-0 z-50 bg-white p-4 overflow-auto' : 'hidden'}
            md:block md:relative md:z-auto md:w-64 md:flex-shrink-0
          `}>
            <div className="flex items-center justify-between mb-4 md:hidden">
              <h2 className="text-lg font-semibold">Filters</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                <X size={20} />
              </Button>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-6 md:sticky md:top-24">
              {/* Categories */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3">Category</h3>
                <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                  <button
                    onClick={() => updateFilter('category', '')}
                    className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                      !filters.category ? 'bg-[#003366] text-white' : 'hover:bg-slate-100'
                    }`}
                    data-testid="filter-all-categories"
                  >
                    All Categories
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => updateFilter('category', cat.id)}
                      className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                        filters.category === cat.id ? 'bg-[#003366] text-white' : 'hover:bg-slate-100'
                      }`}
                      data-testid={`filter-category-${cat.id}`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price range slider */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3">Price Range</h3>
                <PriceRangeSlider
                  min={priceBounds.min}
                  max={priceBounds.max}
                  valueMin={filters.minPrice ?? priceBounds.min}
                  valueMax={filters.maxPrice ?? priceBounds.max}
                  onChange={(lo, hi) => {
                    setFilters(prev => ({ ...prev, minPrice: lo, maxPrice: hi }));
                    const newParams = new URLSearchParams(searchParams);
                    if (lo > priceBounds.min) newParams.set('minPrice', lo); else newParams.delete('minPrice');
                    if (hi < priceBounds.max) newParams.set('maxPrice', hi); else newParams.delete('maxPrice');
                    setSearchParams(newParams);
                  }}
                />
              </div>

              {/* Checkboxes */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3">Filter By</h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={filters.deal}
                      onCheckedChange={(checked) => updateFilter('deal', checked)}
                      data-testid="filter-deals"
                    />
                    <span className="text-sm">Deals Only</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={filters.featured}
                      onCheckedChange={(checked) => updateFilter('featured', checked)}
                      data-testid="filter-featured"
                    />
                    <span className="text-sm">Featured Only</span>
                  </label>
                </div>
              </div>

              {/* Clear filters */}
              {activeFilterCount > 0 && (
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="w-full"
                  data-testid="clear-filters-btn"
                >
                  Clear All Filters
                </Button>
              )}
            </div>

            <div className="mt-4 md:hidden">
              <Button onClick={() => setShowFilters(false)} className="w-full bg-[#003366] hover:bg-[#002855]">
                Apply Filters
              </Button>
            </div>
          </aside>

          {/* Products grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-lg p-4 animate-pulse">
                    <div className="aspect-square bg-slate-200 rounded mb-4" />
                    <div className="h-4 bg-slate-200 rounded mb-2" />
                    <div className="h-4 bg-slate-200 rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-500 text-lg mb-4">No products found</p>
                <Button onClick={clearFilters} variant="outline">Clear Filters</Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="products-grid">
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>

                {/* Infinite scroll sentinel */}
                <div ref={sentinelRef} className="h-4" />

                {loadingMore && (
                  <div className="flex items-center justify-center py-8" data-testid="loading-more">
                    <Loader2 className="animate-spin text-[#003366] mr-2" size={24} />
                    <span className="text-slate-500">Loading more products...</span>
                  </div>
                )}

                {!hasMore && products.length > 0 && (
                  <p className="text-center text-sm text-slate-400 py-6" data-testid="end-of-products">
                    You've seen all {total} products
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Scroll to top */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 w-10 h-10 rounded-full bg-[#003366] text-white shadow-lg flex items-center justify-center hover:bg-[#002855] transition-colors z-40"
          data-testid="scroll-to-top-btn"
        >
          <ChevronUp size={20} />
        </button>
      )}

      <AgeVerificationModal
        isOpen={showAgeModal}
        onConfirm={handleAgeVerified}
        onCancel={handleAgeCanceled}
      />
    </div>
  );
}
